import BaseRepository from "~/api/BaseRepository";
import type { $Fetch } from "nitropack";
import type { SpokenLanguage } from "~/help/languages";

export enum ActorAs {
  Participant = 'participant',
  Company = 'company',
  BusinessProvider = 'business_provider',
}

export interface ListMissionsQueryParams {
  limit?: number;
  offset?: number;
  company_id?: string;
  status?: MissionStatus;
  me?: boolean;
  actor_as?: ActorAs;
}

export interface GetMissionQueryParams {
  includes?: string;
}

export interface MissionResponse {
  id: string;
  object: string;
  created_at: string;
  updated_at: string;
  company_siren: string,
  company_name: string,
  budget: number,
  commission: number,
  description: string,
  title: string,
  is_public: boolean,
  status: MissionStatus,
  emitted: boolean,
  mobility_type?: string,
  has_applied?: boolean,
  application_status?: string | null,
  /** User ID of the mission creator (for BP detection) */
  created_by?: string,
  /** True when the mission is a Pass'D (emitted by a sales / non-organization creator). */
  is_passd?: boolean,
  industry?: {
    industry_id: string,
    name: string,
  },
  company_size?: {
    company_size_id: string,
    name: string,
    min_people?: number | null,
    max_people?: number | null,
  },
  market_segments?: Array<{
    market_segment_id: string,
    name: string,
    min_employees?: number | null,
    max_employees?: number | null,
  }>,
  personas?: Array<{
    persona_id: string,
    name: string,
  }>,
  deal_sizes?: Array<{
    deal_size_id: string,
    name: string,
    min_amount?: number | null,
    max_amount?: number | null,
  }>,
  skills?: Array<{
    skill_id: string,
    name: string,
    description?: string,
  }>,
  /** Included when ?includes=company is passed. Contains the client company details. */
  company?: {
    company_id: string,
    business_name?: string,
    public_name?: string,
    siren?: string,
    siret?: string,
    contact?: {
      first_name?: string,
      last_name?: string,
      email?: string,
    },
  },
  /** Timestamp when company requested mission completion. NULL means no pending request. */
  completion_requested_at?: string,
  /** Required spoken languages (ticket #271). Defaults to ["fr"] server-side. */
  languages?: SpokenLanguage[],
}

export interface ListMissionsResponse {
  missions: MissionResponse[];
  total: number;
}

export enum MissionStatus {
  DRAFT = "draft",
  OPEN = "open",
  ACTIVE = "active",
  COMPLETION_OPEN = "completion_open",
  COMPLETED = "completed",
  CANCELED = "canceled",
  FILLED = "filled"
}

export interface Mission {
  id: string;
  object: string;
  created_at: string;
  updated_at: string;
  is_public: boolean;
}

export interface UpdateMissionContext {
  industry_id?: string;
  company_size_id?: string;
  market_segment_ids?: string[];
  persona_ids?: string[];
  deal_size_ids?: string[];
}

export interface UpdateMissionPayload {
  name?: string;
  description?: string;
  budget?: number;
  is_public?: boolean;
  contract_type?: string;
  business_provider?: {
    business_provider_id?: string;
    rate?: number;
  };
  context?: UpdateMissionContext;
  skills?: string[];
  languages?: SpokenLanguage[];
}

export interface CreateMissionPayload {
  name: string;
  description?: string;
  siren?: string;
  company_name?: string;
  contact_email?: string;
  business_provider?: {
    business_provider_id?: string;
    rate?: number
  };
  budget?: number;
  is_public?: boolean;
  mobility_type?: string;
  contract_type?: string;
  deal_sizes?: string[];
  personas?: string[];
  skills?: string[];
  market_segments?: string[];
  company_size?: string;
  industry?: string;
  languages?: SpokenLanguage[];
}

export interface UpdateMissionFeedbackPayload {
  score?: number;
  public_comment?: string;
  private_comment?: string;
}

export interface MatchingUserResponse {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  score: number;
  handle: string | null;
}

export interface GetMatchingUsersParams {
  min_score?: number;
  limit?: number;
  offset?: number;
}

export const MissionRepository = (client: $Fetch) => ({
  ...BaseRepository(client)('v2/missions'),

  create: (payload: CreateMissionPayload, companyId?: string, options: { skipGlobalErrorAlert?: boolean } = {}) => {
    const url = companyId ? `/v2/missions?company_id=${companyId}` : '/v2/missions';
    return client(url, {
      method: 'POST',
      body: payload,
      headers: {
        "Content-Type": "application/json",
        "accept": "application/json"
      },
      // Let callers surface their own friendly message (e.g. the "[NON DIFFUSIBLE]"
      // company guard) instead of the raw backend error key.
      ...options,
    }) as Promise<MissionResponse>;
  },
  getOneById: (missionId: string, params: GetMissionQueryParams = {}) => {
    // Convert the params object to query parameters
    const queryParams = new URLSearchParams();
    
    // Add includes parameter if provided
    if (params.includes) {
      queryParams.append('includes', params.includes);
    }
    
    // Build the query string
    const queryString = queryParams.toString();
    const url = `/v2/missions/${missionId}${queryString ? `?${queryString}` : ''}`;
    
    return client(url, {
      method: 'GET',
      headers: {
        "accept": "application/json"
      }
    }) as Promise<MissionResponse>;
  },
  updateMissionFeedback: (token: string, payload: UpdateMissionFeedbackPayload) => {
    return client(`/v2/mission-feedback/${token}`, {
      method: 'PATCH',
      body: payload,
      headers: {
        "Content-Type": "application/json",
        "accept": "application/json"
      }
    }) as Promise<any>; // Adjust return type as needed
  },
  getMatchingUsers: (missionId: string, params: GetMatchingUsersParams = {}) => {
    const queryParams = new URLSearchParams();
    if (params.min_score !== undefined) {
      queryParams.append('min_score', params.min_score.toString());
    }
    if (params.limit !== undefined) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params.offset !== undefined) {
      queryParams.append('offset', params.offset.toString());
    }
    const queryString = queryParams.toString();
    const url = `/v2/missions/${missionId}/matching-users${queryString ? `?${queryString}` : ''}`;

    return client(url, {
      method: 'GET',
      headers: {
        "accept": "application/json"
      }
    }) as Promise<MatchingUserResponse[]>;
  }
});